import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { z } from 'zod';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { AppBarStyles, SidePanelStyles } from '@client/style/style.constants';
import { useDesignRightPanelWidth } from '@features/design/design-right-panel-resize.hook';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { useKeydownListener } from '@client/hooks/use-events.hooks';
import amplitudeLog from '@client/amplitude';
import { AMP_PROJECT_SELECT } from '@shared/constants';
import { openProjectWithAnimation } from './lists/helpers/project-open-animation.helpers';
import { useNavigate } from 'react-router-dom';
import { Page } from '@client/store/ui/ui.model';
import {
  registerProjectOpeningMap,
  useProjectOpening,
  useProjectClosing,
  notifyProjectClosingListRendered,
} from './project-opening.store';
import {
  registerProjectListCamera,
  setProjectClosingLayout,
  waitForMapMoveEnd,
} from '@features/design/helpers/map-camera-handoff.helpers';
import {
  createProjectMapGradientLayer,
  PROJECT_MAP_GRADIENT_LAYER_ID,
  PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID,
  PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID,
} from './helpers/project-map-gradient.helpers';
import {
  clusterProjectNames,
  resolveProjectMapHoverTooltip,
  type ProjectMapTooltipPoint,
} from './helpers/project-list-map-hover-tooltip.helpers';
import mapboxgl from 'mapbox-gl';
import { useConfig } from '@client/providers/ConfigProvider';
import { useProjectState, useProjectsForOverview } from '@client/store/project';
import { Coordinate } from '@shared/models/geography.model';
import type { FeatureCollection, MultiPolygon, Point } from 'geojson';
import { mapConfigDefaults } from '@features/design/helpers/map-config.constants';
import { bindMapboxGlStyleLoadRecovery } from '@features/design/helpers/mapbox-style-load.helpers';
import { MiddleMousePanController } from '@features/design/middle-mouse-pan.controller';
import { useProjectListView } from './project-list-view.context';
import { bindDisableImportedLineProgressEvaluation } from '@features/design/helpers/map-basemap-line-progress.helpers';
import { roundProjectFootprint } from './helpers/project-footprint.helpers';
import { resolveProjectListMapCursor } from './helpers/project-list-map-cursor.helpers';
import { getMetersPerPixelAtCoordinate } from '@features/design/helpers/map.helpers';
import {
  canvasColors,
  BUILDING_SELECTION_OUTLINE_WIDTH_PX,
  BUILDING_SELECTION_HALO_WIDTH_PX,
  LOCATION_PIN_METERS_PER_PIXEL,
} from '@features/design/map.constants';

import {
  ProjectMapContext,
  SelectedProjectMapContext,
} from './project-list-map.context';
const PROJECT_MAP_GRADIENT_COLOR = '#E2E2D6';
const locationResponseSchema = z.object({
  features: z.array(
    z.object({
      text: z.string(),
      context: z
        .array(z.object({ id: z.string(), text: z.string() }))
        .optional(),
    }),
  ),
});
const placeNames = new Map<string, string>();

const PROJECT_MAP_POINTER_LAYERS = [
  'project-selected-fill',
  'project-clusters',
  'project-cluster-count',
  'project-pins',
  'project-fills',
] as const;

type ProjectMapPointerEvent = {
  point: { x: number; y: number };
  originalEvent: { clientX: number; clientY: number };
};

/**
 * Topmost pin, cluster, or footprint under the pointer.
 * Screen position follows the displayed canvas, including scale and offset.
 */
const projectMapFeatureAtPointer = (
  instance: mapboxgl.Map,
  event: ProjectMapPointerEvent,
) => {
  const canvas = instance.getCanvas();
  const layers = PROJECT_MAP_POINTER_LAYERS.filter((id) =>
    instance.getLayer(id),
  );
  if (!layers.length) return;
  const bounds = canvas.getBoundingClientRect();
  const { clientX, clientY } = event.originalEvent;
  // Match hit testing to the displayed canvas, including browser scaling
  // and layout offsets, rather than a stale map event coordinate.
  const point =
    bounds.width > 0 &&
    bounds.height > 0 &&
    guards.is.finiteNumber(clientX) &&
    guards.is.finiteNumber(clientY)
      ? {
          x: ((clientX - bounds.left) / bounds.width) * canvas.clientWidth,
          y: ((clientY - bounds.top) / bounds.height) * canvas.clientHeight,
        }
      : event.point;
  return instance.queryRenderedFeatures(
    [
      [point.x - 2, point.y - 2],
      [point.x + 2, point.y + 2],
    ],
    { layers: [...layers] },
  )[0];
};

/** Read-only map under the project list; previewing does not change the active project. */
export const ProjectListMap = ({
  children,
  contentReady = true,
}: {
  children: React.ReactNode;
  contentReady?: boolean;
}) => {
  const [config] = useConfig();
  const navigateTo = useProjectNavigation();
  const navigate = useNavigate();
  const { fetchProject } = useProjectState('fetchProject');
  const { view } = useProjectListView();
  const viewRef = useRef(view);
  viewRef.current = view;
  const designRightPanelWidth = useDesignRightPanelWidth();
  const projects = useProjectsForOverview();
  const previewsById = useMemo(
    () => new Map(projects.map((project) => [project.id, project.map_preview])),
    [projects],
  );
  const pins = useMemo<FeatureCollection<Point>>(
    () => ({
      type: 'FeatureCollection',
      features: projects.flatMap((project) => {
        const coordinate = project.map_preview?.location;
        if (
          !coordinate ||
          !Number.isFinite(coordinate.lat) ||
          !Number.isFinite(coordinate.lng)
        )
          return [];
        return [
          {
            type: 'Feature',
            properties: { projectId: project.id, projectName: project.name },
            geometry: {
              type: 'Point',
              coordinates: [coordinate.lng, coordinate.lat],
            },
          },
        ];
      }),
    }),
    [projects],
  );
  const rawOutlines = useMemo<FeatureCollection<MultiPolygon>>(
    () => ({
      type: 'FeatureCollection',
      features: projects.flatMap((project) =>
        (project.map_preview?.outlines.features ?? []).map(
          (feature, index) => ({
            ...feature,
            id: `${project.id}:${index}`,
            properties: {
              ...feature.properties,
              projectId: project.id,
              projectName: project.name,
              footprintId: `${project.id}:${index}`,
            },
          }),
        ),
      ),
    }),
    [projects],
  );
  const outlines = useMemo<FeatureCollection<MultiPolygon>>(
    () => ({
      ...rawOutlines,
      features: rawOutlines.features.map((feature) => ({
        ...feature,
        geometry: roundProjectFootprint(feature.geometry),
      })),
    }),
    [rawOutlines],
  );
  const { project } = useProjectState('project');
  const initialLocation = useRef(
    project.design?.location ?? { lat: 59.3293, lng: 18.0686 },
  );
  const [location, setLocation] = useState<Coordinate>();
  const returningProjectId = useRef(
    useProjectClosing.getState().projectId?.toString(),
  );
  const [fitProjectId, setFitProjectId] = useState<string | undefined>(
    returningProjectId.current,
  );
  // Geometry and selection ids can stay unchanged; every card click still refits.
  const [previewRequest, setPreviewRequest] = useState(0);
  const [cameraReset, setCameraReset] = useState(0);
  const appliedCameraReset = useRef(0);
  const overviewFitZoom = useRef<number | undefined>(undefined);
  const overviewZoom = useRef<number | undefined>(undefined);
  const [selectedProjectId, setSelectedProjectId] = useState<
    string | undefined
  >(returningProjectId.current);
  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;
  const [openTooltipPoint, setOpenTooltipPoint] = useState<
    ProjectMapTooltipPoint | undefined
  >();
  const container = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const mapDraggingRef = useRef(false);
  /** Hit under the pointer, including while a button is held, for dragend. */
  const pointerOverFeatureRef = useRef(false);
  const renderedOutlines = useRef<FeatureCollection<MultiPolygon> | null>(null);
  /** Clear map selection and return the camera to the organisation overview. */
  const clearProjectSelection = useCallback(
    (options?: { backgroundClick: boolean }) => {
      const instance = map.current;
      const currentZoom = instance?.getZoom();
      const zoomOut =
        options?.backgroundClick &&
        !selectedProjectIdRef.current &&
        currentZoom !== undefined &&
        overviewFitZoom.current !== undefined &&
        currentZoom <= overviewFitZoom.current + 0.05;
      const minZoom = instance?.getMinZoom() ?? 0;
      overviewZoom.current = zoomOut
        ? Math.max(
            minZoom,
            Math.min(overviewZoom.current ?? currentZoom, currentZoom) - 1,
          )
        : undefined;
      setSelectedProjectId(undefined);
      setFitProjectId(undefined);
      setLocation(undefined);
      setCameraReset((value) => value + 1);
    },
    [],
  );
  const preview = useCallback(
    (id: string) => {
      overviewZoom.current = undefined;
      setSelectedProjectId(id);
      const projectPreview = previewsById.get(id);
      setFitProjectId(
        projectPreview?.outlines.features.length ? id : undefined,
      );
      setPreviewRequest((request) => request + 1);
      const coordinate = projectPreview?.location;
      if (
        coordinate &&
        Number.isFinite(coordinate.lat) &&
        Number.isFinite(coordinate.lng)
      ) {
        // Request a camera move for every click, including the open project's
        // location reused by its compact list data after returning from Design.
        setLocation({ ...coordinate });
      }
    },
    [previewsById],
  );
  useKeydownListener((event) => {
    if (!selectedProjectIdRef.current) return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('input, textarea, [contenteditable="true"]')
    )
      return;
    // Leave Escape to open menus/dialogs; ignore closed keepMounted portals.
    if (
      document.querySelector(
        '.MuiModal-root:not([aria-hidden="true"]), .MuiPopover-root:not([aria-hidden="true"]), .MuiMenu-root:not([aria-hidden="true"]), [role="dialog"][aria-modal="true"]:not([aria-hidden="true"])',
      )
    )
      return;
    event.preventDefault();
    clearProjectSelection();
  }, 'Escape');

  useEffect(() => {
    if (
      selectedProjectId &&
      !projects.some(({ id }) => id === selectedProjectId)
    ) {
      setSelectedProjectId(undefined);
      setLocation(undefined);
      setFitProjectId(undefined);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    if (!container.current || !config.mapboxAccessToken) return;
    const closingCamera = useProjectClosing.getState().camera;
    const instance = new mapboxgl.Map({
      ...mapConfigDefaults,
      container: container.current,
      accessToken: config.mapboxAccessToken,
      center: [initialLocation.current.lng, initialLocation.current.lat],
      pitch: 0,
      bearing: 0,
      maxPitch: 0,
      maxZoom: closingCamera ? Math.max(18.5, closingCamera.zoom) : 17,
      ...(closingCamera && {
        center: closingCamera.center,
        zoom: closingCamera.zoom,
        pitch: closingCamera.pitch,
        bearing: closingCamera.bearing,
        padding: closingCamera.padding,
        projection: mapConfigDefaults.projection,
      }),
      // Cluster counts must appear immediately with their circles when resize
      // or camera fitting replaces the source tiles; they are not map labels.
      fadeDuration: 0,
      interactive: false,
      attributionControl: true,
    });
    if (closingCamera)
      instance.setVerticalFieldOfView(closingCamera.verticalFieldOfView);
    map.current = instance;
    const unregisterCamera = registerProjectListCamera(instance);
    bindDisableImportedLineProgressEvaluation(instance);
    // Mapbox prints an unlistened error to console.error; Sentry captures that line.
    const basemapStyle = mapConfigDefaults.style;
    const unbindStyleLoadRecovery = guards.is.string(basemapStyle)
      ? bindMapboxGlStyleLoadRecovery(instance, {
          style: basemapStyle,
          config: mapConfigDefaults.config,
        })
      : undefined;
    return () => {
      unbindStyleLoadRecovery?.();
      unregisterCamera();
      map.current = null;
      instance.remove();
    };
  }, [config.mapboxAccessToken]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const scrollRoot = container.current?.closest<HTMLElement>(
      '[data-project-list-page]',
    );
    let verticalGradientOpacity = 1;
    const updateVerticalGradientOpacity = () => {
      const opacity = Math.max(
        0,
        Math.min(
          1,
          1 - (scrollRoot?.scrollTop ?? 0) / (AppBarStyles.HEIGHT * 4),
        ),
      );
      if (opacity === verticalGradientOpacity) return;
      verticalGradientOpacity = opacity;
      instance.triggerRepaint();
    };
    updateVerticalGradientOpacity();
    scrollRoot?.addEventListener('scroll', updateVerticalGradientOpacity, {
      passive: true,
    });
    const draw = () => {
      if (!instance.getLayer(PROJECT_MAP_GRADIENT_LAYER_ID))
        instance.addLayer(
          createProjectMapGradientLayer(
            PROJECT_MAP_GRADIENT_COLOR,
            () => true,
            { opacity: 0.6 },
          ),
          instance.getLayer('project-fills') ? 'project-fills' : undefined,
        );
      if (!instance.getLayer(PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID))
        instance.addLayer(
          createProjectMapGradientLayer(
            PROJECT_MAP_GRADIENT_COLOR,
            () => true,
            {
              id: PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID,
              opacity: () => (viewRef.current === 'List' ? 0.6 : 0.3),
              // endOpacity is relative to the layer's start opacity.
              endOpacity: () => (viewRef.current === 'List' ? 2 / 3 : 0),
            },
          ),
        );
      if (!instance.getLayer(PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID))
        instance.addLayer(
          createProjectMapGradientLayer(
            PROJECT_MAP_GRADIENT_COLOR,
            () => true,
            {
              id: PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID,
              opacity: () => verticalGradientOpacity,
              opacityStops: [0.5, 0.48, 0.2, 0.13, 0],
              stopPositions: [0, 0.1, 0.2, 0.3, 1],
              direction: 'down',
            },
          ),
        );
    };
    if (instance.isStyleLoaded()) draw();
    instance.on('style.load', draw);
    return () => {
      instance.off('style.load', draw);
      scrollRoot?.removeEventListener('scroll', updateVerticalGradientOpacity);
    };
  }, [config.mapboxAccessToken]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => map.current?.resize());
    map.current?.triggerRepaint();
    return () => cancelAnimationFrame(frame);
  }, [view]);

  useEffect(() => {
    const instance = map.current;
    const mapContainer = container.current;
    if (!instance || !mapContainer || view !== 'Map') return;
    // Use Design's native Mapbox LMB pan and pointer-centred wheel zoom,
    // plus its screen-delta pan controller for RMB, without rotation.
    instance.dragPan.enable();
    instance.scrollZoom.enable();
    const canvas = instance.getCanvas();
    canvas.style.cursor = 'grab';
    const onDragStart = () => {
      mapDraggingRef.current = true;
      canvas.style.cursor = 'grabbing';
    };
    const onDragEnd = (event?: { originalEvent?: Event }) => {
      mapDraggingRef.current = false;
      const originalEvent = event?.originalEvent;
      const bounds = canvas.getBoundingClientRect();
      // Mapbox may not emit mousemove during the pan, so re-read the release point.
      if (
        originalEvent instanceof MouseEvent &&
        bounds.width > 0 &&
        bounds.height > 0
      ) {
        pointerOverFeatureRef.current = Boolean(
          projectMapFeatureAtPointer(instance, {
            point: { x: 0, y: 0 },
            originalEvent,
          }),
        );
      }
      canvas.style.cursor = resolveProjectListMapCursor({
        dragging: false,
        overFeature: pointerOverFeatureRef.current,
      });
    };
    instance.on('dragstart', onDragStart);
    instance.on('dragend', onDragEnd);
    const rightPan = new MiddleMousePanController(2);
    rightPan.setCursorHandler((cursor) => {
      canvas.style.cursor =
        cursor ??
        resolveProjectListMapCursor({
          dragging: mapDraggingRef.current,
          overFeature: pointerOverFeatureRef.current,
        });
    });
    rightPan.attach(instance);
    const preventContextMenu = (event: MouseEvent) => event.preventDefault();
    canvas.addEventListener('contextmenu', preventContextMenu);
    const scrollRoot = mapContainer.closest<HTMLElement>(
      '[data-project-list-page]',
    );
    const wheelSurface = scrollRoot ?? mapContainer;
    const forwardedWheels = new WeakSet<WheelEvent>();
    const isolateWheel = (event: WheelEvent) => {
      if (forwardedWheels.has(event)) return;
      // Pinch is ctrl+wheel. Cmd (metaKey) and Shift zoom the same way, and a
      // stronger horizontal delta still zooms when none of those are held.
      const modifierZoom = event.ctrlKey || event.metaKey || event.shiftKey;
      const zoomGesture =
        modifierZoom || Math.abs(event.deltaX) > Math.abs(event.deltaY);
      if (
        !zoomGesture &&
        event.target instanceof Node &&
        !mapContainer.contains(event.target)
      )
        return;
      const scale =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? (scrollRoot?.clientHeight ?? mapContainer.clientHeight)
            : 1;
      event.preventDefault();
      event.stopPropagation();
      if (!zoomGesture) {
        if (scrollRoot) scrollRoot.scrollTop += event.deltaY * scale;
        return;
      }
      const delta =
        (modifierZoom ? event.deltaY || event.deltaX : event.deltaX) * scale;
      // Keep Mapbox's projection-aware camera handling. Repeated easeTo(around)
      // drifts during the globe/Mercator transition at overview zoom levels.
      const forwarded = new WheelEvent('wheel', {
        bubbles: true,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY,
        // Cmd arrives as metaKey. Forward it as ctrlKey, same as pinch.
        ctrlKey: event.ctrlKey || event.metaKey,
        deltaY: delta,
        deltaMode: WheelEvent.DOM_DELTA_PIXEL,
      });
      forwardedWheels.add(forwarded);
      canvas.dispatchEvent(forwarded);
    };
    wheelSurface.addEventListener('wheel', isolateWheel, {
      passive: false,
      capture: true,
    });
    return () => {
      mapDraggingRef.current = false;
      rightPan.detach();
      canvas.removeEventListener('contextmenu', preventContextMenu);
      wheelSurface.removeEventListener('wheel', isolateWheel, true);
      instance.off('dragstart', onDragStart);
      instance.off('dragend', onDragEnd);
      canvas.style.cursor = '';
      instance.dragPan.disable();
      instance.scrollZoom.disable();
      instance.stop();
    };
  }, [view, config.mapboxAccessToken]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || view !== 'Map') return;
    const canvas = instance.getCanvas();
    let active = true;
    let expansionRequest = 0;
    let hoverRequest = 0;
    let hoveredClusterId: number | undefined;
    let clusterNames: string[] | undefined;
    let backgroundClickTimeout: ReturnType<typeof setTimeout> | undefined;
    const cancelBackgroundClick = () => {
      clearTimeout(backgroundClickTimeout);
      backgroundClickTimeout = undefined;
    };
    const clearClusterHover = () => {
      hoverRequest++;
      hoveredClusterId = undefined;
      clusterNames = undefined;
    };
    const cancelExpansion = () => {
      cancelBackgroundClick();
      expansionRequest++;
      clearClusterHover();
      setOpenTooltipPoint(undefined);
    };
    const featureAtPointer = (event: mapboxgl.MapMouseEvent) =>
      projectMapFeatureAtPointer(instance, event);
    const selectProject = (event: mapboxgl.MapMouseEvent) => {
      if (event.originalEvent.button !== 0) return;
      cancelBackgroundClick();
      const feature = featureAtPointer(event);
      cancelExpansion();
      if (!feature) {
        // Wait for a possible double-click before moving the camera outward.
        backgroundClickTimeout = setTimeout(() => {
          clearProjectSelection({ backgroundClick: true });
        }, 300);
        return;
      }
      const request = expansionRequest;
      const clusterId = feature?.properties?.cluster_id;
      const count = feature?.properties?.point_count;
      if (typeof clusterId === 'number' && typeof count === 'number') {
        const source = instance.getSource('project-pins');
        if (source?.type !== 'geojson') return;
        source.getClusterLeaves(clusterId, count, 0, (error, leaves) => {
          if (
            error ||
            !leaves?.length ||
            !active ||
            request !== expansionRequest
          )
            return;
          const bounds = new mapboxgl.LngLatBounds();
          for (const leaf of leaves) {
            if (leaf.geometry?.type === 'Point') {
              const [lng, lat] = leaf.geometry.coordinates;
              if (typeof lng === 'number' && typeof lat === 'number')
                bounds.extend([lng, lat]);
            }
            const preview = previewsById.get(leaf.properties?.projectId);
            for (const outline of preview?.outlines.features ?? []) {
              for (const [lng, lat] of outline.geometry.coordinates.flat(2)) {
                if (typeof lng === 'number' && typeof lat === 'number')
                  bounds.extend([lng, lat]);
              }
            }
          }
          if (bounds.isEmpty()) return;
          const header = document.querySelector('[data-app-header]');
          const headerRect = header?.getBoundingClientRect();
          const leftWidth =
            container.current
              ?.closest('[data-project-list-page]')
              ?.querySelector('[data-project-list-column]')
              ?.getBoundingClientRect().width ?? 0;
          const padding = {
            top: (headerRect?.bottom ?? 0) + 64,
            left: leftWidth + 64,
            right: 64,
            bottom: 64,
          };
          const camera = instance.cameraForBounds(bounds, {
            bearing: 0,
            padding,
            maxZoom: 17,
          });
          if (camera) {
            const reducedMotion = window.matchMedia(
              '(prefers-reduced-motion: reduce)',
            ).matches;
            const target = {
              ...camera,
              padding,
              pitch: 0,
              bearing: 0,
              duration: reducedMotion ? 0 : 900,
              retainPadding: false,
            };
            if (reducedMotion) {
              instance.easeTo(target);
              return;
            }
            instance.flyTo({
              ...target,
              curve: 1,
              easing: (progress) => progress * progress * (3 - 2 * progress),
            });
          }
        });
        return;
      }
      const projectId = feature?.properties?.projectId;
      if (typeof projectId !== 'string') return;
      if (!projectId) return;
      if (
        projectId === selectedProjectIdRef.current &&
        (feature.geometry.type === 'Polygon' ||
          feature.geometry.type === 'MultiPolygon')
      ) {
        openFootprint(event, feature);
        return;
      }
      // Re-clicking the selected pin clears the frame (footprint still opens).
      if (
        projectId === selectedProjectIdRef.current &&
        feature.geometry?.type === 'Point'
      ) {
        clearProjectSelection();
        return;
      }
      setSelectedProjectId(projectId);
      if (feature?.geometry?.type === 'Point') {
        const coordinate = previewsById.get(projectId)?.location;
        if (coordinate) {
          setFitProjectId(projectId);
          setLocation({ ...coordinate });
        }
      }
      const scrollport = container.current?.closest('[data-project-list-page]');
      const row = scrollport?.querySelector<HTMLElement>(
        `[data-project-list-row-id="${CSS.escape(projectId)}"]`,
      );
      if (!row) return;
      scrollport?.dispatchEvent(new Event('project-list-reveal-row'));
      row.scrollIntoView({
        block: 'nearest',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'instant'
          : 'smooth',
      });
    };
    let hoveredFootprintId: string | number | undefined;
    const setHoveredFootprint = (id: string | number | undefined) => {
      if (hoveredFootprintId === id) return;
      if (hoveredFootprintId !== undefined)
        instance.setFeatureState(
          { source: 'project-outlines', id: hoveredFootprintId },
          { hover: false },
        );
      hoveredFootprintId = id;
      if (id !== undefined)
        instance.setFeatureState(
          { source: 'project-outlines', id },
          { hover: true },
        );
    };
    const hoverProject = (event: mapboxgl.MapMouseEvent) => {
      const feature = featureAtPointer(event);
      pointerOverFeatureRef.current = Boolean(feature);
      // Button-held moves skip tooltips, but the hit is kept for dragend.
      if (event.originalEvent.buttons || mapDraggingRef.current) {
        if (mapDraggingRef.current) canvas.style.cursor = 'grabbing';
        return;
      }
      canvas.style.cursor = resolveProjectListMapCursor({
        dragging: false,
        overFeature: Boolean(feature),
      });
      setHoveredFootprint(
        feature?.geometry.type === 'Polygon' ||
          feature?.geometry.type === 'MultiPolygon'
          ? feature.id
          : undefined,
      );
      const anchor = {
        x: event.originalEvent.clientX,
        y: event.originalEvent.clientY,
      };
      const hoverTooltip = resolveProjectMapHoverTooltip(
        feature,
        anchor,
        selectedProjectIdRef.current,
      );
      switch (hoverTooltip.kind) {
        case 'cluster': {
          const { clusterId, count } = hoverTooltip;
          if (hoveredClusterId !== clusterId) {
            clearClusterHover();
            hoveredClusterId = clusterId;
            setOpenTooltipPoint(undefined);
            const request = hoverRequest;
            const source = instance.getSource('project-pins');
            if (source?.type === 'geojson') {
              source.getClusterLeaves(clusterId, count, 0, (error, leaves) => {
                if (error || !active || request !== hoverRequest) return;
                clusterNames = clusterProjectNames(leaves ?? []);
                setOpenTooltipPoint(
                  clusterNames.length
                    ? { ...anchor, projectNames: clusterNames }
                    : undefined,
                );
              });
            }
          } else if (clusterNames?.length) {
            setOpenTooltipPoint({ ...anchor, projectNames: clusterNames });
          }
          return;
        }
        case 'tooltip':
          clearClusterHover();
          setOpenTooltipPoint(hoverTooltip.point);
          return;
        case 'hidden':
          clearClusterHover();
          setOpenTooltipPoint(undefined);
          return;
        default:
          assertUnreachable(hoverTooltip);
      }
    };
    const openFootprint = (
      event: mapboxgl.MapMouseEvent,
      feature = featureAtPointer(event),
    ) => {
      if (
        feature?.geometry?.type !== 'Polygon' &&
        feature?.geometry?.type !== 'MultiPolygon'
      )
        return;
      const projectId = feature.properties?.projectId;
      if (typeof projectId !== 'string' || !projectId) return;
      event.preventDefault();
      cancelExpansion();
      setOpenTooltipPoint(undefined);
      const source =
        container.current
          ?.closest('[data-project-list-page]')
          ?.querySelector(
            `[data-project-list-row-id="${CSS.escape(projectId)}"] [data-project-map-card]`,
          ) ?? container.current;
      if (!source) return;
      amplitudeLog(AMP_PROJECT_SELECT, { ProjectId: projectId });
      const listed = projects.find(({ id }) => String(id) === projectId);
      const versionId = listed?.active_version_id ?? listed?.versionIds[0];
      void openProjectWithAnimation(
        source,
        (cameraReady) =>
          fetchProject(Number(projectId), undefined, cameraReady),
        () =>
          navigateTo(
            { projectId, versionId, page: Page.Design },
            { flushSync: true },
          ),
        Number(projectId),
        (url) => navigate(url, { flushSync: true }),
      );
    };
    const doubleClick = (event: mapboxgl.MapMouseEvent) => {
      cancelBackgroundClick();
      const feature = featureAtPointer(event);
      if (feature) {
        openFootprint(event, feature);
        return;
      }
      event.preventDefault();
      cancelExpansion();
      overviewZoom.current = undefined;
      // Mapbox stops camera animations after dispatching a prevented dblclick.
      queueMicrotask(() => {
        if (!active) return;
        instance.easeTo({
          zoom: Math.min(instance.getZoom() + 1, instance.getMaxZoom()),
          around: event.lngLat,
          duration: window.matchMedia('(prefers-reduced-motion: reduce)')
            .matches
            ? 0
            : 500,
        });
      });
    };
    const clearHover = (event: MouseEvent) => {
      clearClusterHover();
      setHoveredFootprint(undefined);
      setOpenTooltipPoint(undefined);
      pointerOverFeatureRef.current = false;
      if (!event.buttons)
        canvas.style.cursor = resolveProjectListMapCursor({
          dragging: mapDraggingRef.current,
          overFeature: false,
        });
    };
    const resetOverviewZoomOnGesture = (
      event: mapboxgl.MapEventOf<'zoomstart'>,
    ) => {
      if ('originalEvent' in event && event.originalEvent)
        overviewZoom.current = undefined;
    };
    instance.on('click', selectProject);
    instance.on('dblclick', doubleClick);
    instance.on('movestart', cancelExpansion);
    instance.on('zoomstart', resetOverviewZoomOnGesture);
    canvas.addEventListener('mousedown', cancelExpansion);
    canvas.addEventListener('wheel', cancelExpansion);
    instance.on('mousemove', hoverProject);
    canvas.addEventListener('mouseleave', clearHover);
    return () => {
      active = false;
      cancelBackgroundClick();
      pointerOverFeatureRef.current = false;
      if (map.current === instance) setHoveredFootprint(undefined);
      cancelExpansion();
      canvas.removeEventListener('mousedown', cancelExpansion);
      canvas.removeEventListener('wheel', cancelExpansion);
      instance.off('click', selectProject);
      instance.off('dblclick', doubleClick);
      instance.off('movestart', cancelExpansion);
      instance.off('zoomstart', resetOverviewZoomOnGesture);
      instance.off('mousemove', hoverProject);
      canvas.removeEventListener('mouseleave', clearHover);
    };
  }, [
    view,
    projects,
    previewsById,
    config.mapboxAccessToken,
    navigateTo,
    navigate,
    fetchProject,
    clearProjectSelection,
  ]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const fillOpacity: mapboxgl.ExpressionSpecification = [
      'case',
      ['==', ['get', 'projectId'], selectedProjectId ?? ''],
      1,
      ['boolean', ['feature-state', 'hover'], false],
      0.35,
      0.15,
    ];
    const updateOpeningOpacity = () => {
      const opening =
        useProjectOpening.getState().phase !== 'idle' ||
        useProjectClosing.getState().phase === 'preparing';
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)')
        .matches
        ? 0
        : useProjectClosing.getState().phase !== 'idle'
          ? 125
          : 250;
      // The opened project's dedicated selection layers stay fully visible.
      if (instance.getLayer('project-fills')) {
        instance.setPaintProperty('project-fills', 'fill-opacity-transition', {
          duration,
        });
        instance.setPaintProperty(
          'project-fills',
          'fill-opacity',
          opening ? 0 : fillOpacity,
        );
      }
      if (instance.getLayer('project-outlines')) {
        instance.setPaintProperty(
          'project-outlines',
          'line-opacity-transition',
          {
            duration,
          },
        );
        instance.setPaintProperty(
          'project-outlines',
          'line-opacity',
          opening ? 0 : 0.15,
        );
      }
    };
    const draw = () => {
      const fillColor: mapboxgl.ExpressionSpecification = [
        'case',
        ['==', ['get', 'projectId'], selectedProjectId ?? ''],
        '#555452',
        '#000000',
      ];
      const source = instance.getSource('project-outlines');
      if (source?.type === 'geojson') {
        if (renderedOutlines.current !== outlines) source.setData(outlines);
        instance.setPaintProperty('project-fills', 'fill-color', fillColor);
      } else if (!source) {
        instance.addSource('project-outlines', {
          type: 'geojson',
          promoteId: 'footprintId',
          tolerance: 0,
          data: outlines,
        });
        instance.addLayer({
          id: 'project-fills',
          type: 'fill',
          source: 'project-outlines',
          slot: 'top',
          paint: {
            'fill-color': fillColor,
            'fill-opacity': fillOpacity,
            'fill-antialias': false,
          },
        });
        instance.addLayer({
          id: 'project-outlines',
          type: 'line',
          source: 'project-outlines',
          slot: 'top',
          layout: { 'line-join': 'round' },
          paint: {
            'line-color': '#000000',
            'line-opacity': 0.15,
            'line-width': 2,
            'line-offset': 1,
          },
        });
      }
      const selectedFilter: mapboxgl.FilterSpecification = [
        '==',
        ['get', 'projectId'],
        selectedProjectId ?? '',
      ];
      if (!instance.getLayer('project-selected-fill')) {
        instance.addLayer({
          id: 'project-selected-fill',
          type: 'fill',
          source: 'project-outlines',
          slot: 'top',
          filter: selectedFilter,
          paint: {
            'fill-color': '#555452',
            'fill-opacity': 1,
            'fill-antialias': false,
          },
        });
        instance.addLayer({
          id: 'project-selected-halo',
          type: 'line',
          source: 'project-outlines',
          slot: 'top',
          filter: selectedFilter,
          layout: { 'line-join': 'round' },
          paint: {
            'line-color': canvasColors.lineSelectedHalo,
            'line-width': BUILDING_SELECTION_HALO_WIDTH_PX,
          },
        });
        instance.addLayer({
          id: 'project-selected-outline',
          type: 'line',
          source: 'project-outlines',
          slot: 'top',
          filter: selectedFilter,
          layout: { 'line-join': 'round' },
          paint: {
            'line-color': canvasColors.lineSelected,
            'line-width': BUILDING_SELECTION_OUTLINE_WIDTH_PX,
          },
        });
      } else {
        instance.setPaintProperty(
          'project-selected-fill',
          'fill-color',
          '#555452',
        );
        instance.setFilter('project-selected-fill', selectedFilter);
        instance.setFilter('project-selected-halo', selectedFilter);
        instance.setFilter('project-selected-outline', selectedFilter);
      }
      updateOpeningOpacity();
      renderedOutlines.current = outlines;
      if (instance.getLayer(PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID))
        instance.moveLayer(PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID);
      if (instance.getLayer(PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID))
        instance.moveLayer(PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID);
    };
    if (instance.getSource('project-outlines') || instance.isStyleLoaded())
      draw();
    instance.on('style.load', draw);
    const unsubscribe = useProjectOpening.subscribe((state, previous) => {
      if ((state.phase === 'idle') !== (previous.phase === 'idle'))
        updateOpeningOpacity();
    });
    const unsubscribeClosing = useProjectClosing.subscribe(
      (state, previous) => {
        if ((state.phase === 'preparing') !== (previous.phase === 'preparing'))
          updateOpeningOpacity();
      },
    );
    return () => {
      unsubscribe();
      unsubscribeClosing();
      instance.off('style.load', draw);
    };
  }, [outlines, selectedProjectId, config.mapboxAccessToken]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    let pinsVisible: boolean | undefined;
    const updateVisibility = () => {
      const center = instance.getCenter();
      const showPins =
        getMetersPerPixelAtCoordinate(instance, {
          lat: center.lat,
          lng: center.lng,
        }) >= LOCATION_PIN_METERS_PER_PIXEL;
      if (showPins === pinsVisible) return;
      pinsVisible = showPins;
      for (const id of [
        'project-fills',
        'project-outlines',
        'project-selected-fill',
        'project-selected-halo',
        'project-selected-outline',
      ]) {
        if (instance.getLayer(id))
          instance.setLayoutProperty(
            id,
            'visibility',
            showPins ? 'none' : 'visible',
          );
      }
      for (const id of [
        'project-pins',
        'project-clusters',
        'project-cluster-count',
      ]) {
        if (instance.getLayer(id))
          instance.setLayoutProperty(
            id,
            'visibility',
            showPins ? 'visible' : 'none',
          );
      }
    };
    const draw = () => {
      const source = instance.getSource('project-pins');
      if (source?.type === 'geojson') source.setData(pins);
      else if (!source) {
        instance.addSource('project-pins', {
          type: 'geojson',
          data: pins,
          cluster: true,
          clusterRadius: 35,
          clusterMaxZoom: 20,
        });
        instance.addLayer({
          id: 'project-clusters',
          type: 'circle',
          source: 'project-pins',
          slot: 'top',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#000000',
            'circle-opacity': 1,
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              12,
              10,
              15,
              100,
              20,
            ],
          },
        });
        instance.addLayer({
          id: 'project-cluster-count',
          type: 'symbol',
          source: 'project-pins',
          slot: 'top',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['to-string', ['get', 'point_count']],
            'text-size': 12,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': '#ffffff' },
        });
        instance.addLayer({
          id: 'project-pins',
          type: 'circle',
          source: 'project-pins',
          slot: 'top',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': '#000000',
            'circle-opacity': 1,
            'circle-radius': 8,
          },
        });
      }
      if (instance.getLayer(PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID))
        instance.moveLayer(PROJECT_MAP_FOREGROUND_GRADIENT_LAYER_ID);
      if (instance.getLayer(PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID))
        instance.moveLayer(PROJECT_MAP_VERTICAL_FOREGROUND_GRADIENT_LAYER_ID);
      pinsVisible = undefined;
      updateVisibility();
    };
    if (instance.getSource('project-outlines') || instance.isStyleLoaded())
      draw();
    instance.on('style.load', draw);
    instance.on('move', updateVisibility);
    return () => {
      instance.off('style.load', draw);
      instance.off('move', updateVisibility);
    };
  }, [pins, config.mapboxAccessToken]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const rendered = () => {
      if (
        contentReady &&
        useProjectClosing.getState().phase === 'preparing' &&
        instance.areTilesLoaded() &&
        renderedOutlines.current === outlines
      ) {
        notifyProjectClosingListRendered();
      }
    };
    instance.on('render', rendered);
    instance.triggerRepaint();
    return () => {
      instance.off('render', rendered);
    };
  }, [outlines, preview, contentReady, config.mapboxAccessToken]);

  useEffect(
    () =>
      registerProjectOpeningMap(async (projectId) => {
        const instance = map.current;
        const preview = previewsById.get(String(projectId));
        if (!instance || !preview) return;
        setSelectedProjectId(String(projectId));
        // Commit the loading UI and selection before starting the camera clock.
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
        );
        const bounds = new mapboxgl.LngLatBounds();
        for (const outline of preview.outlines.features) {
          for (const [lng, lat] of outline.geometry.coordinates.flat(2))
            if (typeof lng === 'number' && typeof lat === 'number')
              bounds.extend([lng, lat]);
        }
        if (bounds.isEmpty() && preview.location) {
          const { lng, lat } = preview.location;
          const latDelta = 250 / 111320;
          const lngDelta = latDelta / Math.cos((lat * Math.PI) / 180);
          bounds.extend([lng - lngDelta, lat - latDelta]);
          bounds.extend([lng + lngDelta, lat + latDelta]);
        }
        if (bounds.isEmpty()) return;
        const header = document.querySelector('[data-app-header]');
        setProjectClosingLayout(
          projectId,
          header?.getBoundingClientRect().bottom ?? 0,
        );
        instance.stop();
        instance.setMaxZoom(18.5);
        // Fit within Design's available width while keeping the camera padding
        // neutral for the handoff. Offset into the space between unequal panels.
        const panelInset = (SidePanelStyles.WIDTH + designRightPanelWidth) / 2;
        const camera = instance.cameraForBounds(bounds, {
          padding: {
            top: 64,
            left: 64 + panelInset,
            right: 64 + panelInset,
            bottom: 104,
          },
          bearing: instance.getBearing(),
          maxZoom: 18.5,
        });
        if (!camera) return;
        const moved = waitForMapMoveEnd(instance);
        instance.flyTo({
          ...camera,
          offset: [(SidePanelStyles.WIDTH - designRightPanelWidth) / 2, -20],
          padding: { top: 0, right: 0, bottom: 0, left: 0 },
          retainPadding: false,
          duration: window.matchMedia('(prefers-reduced-motion: reduce)')
            .matches
            ? 0
            : 900,
        });
        await moved;
      }),
    [previewsById, designRightPanelWidth],
  );

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const move = () => {
      if (
        !contentReady ||
        useProjectOpening.getState().phase !== 'idle' ||
        (useProjectClosing.getState().phase === 'preparing' &&
          useProjectClosing.getState().camera !== undefined)
      )
        return;
      const header = document.querySelector('[data-app-header]');
      const column = container.current
        ?.closest('[data-project-list-page]')
        ?.querySelector('[data-project-list-column]');
      const projectOutlines = fitProjectId
        ? previewsById.get(fitProjectId)?.outlines.features
        : undefined;
      if (projectOutlines?.length) {
        const bounds = new mapboxgl.LngLatBounds();
        for (const outline of projectOutlines) {
          for (const [lng, lat] of outline.geometry.coordinates.flat(2)) {
            if (typeof lng === 'number' && typeof lat === 'number')
              bounds.extend([lng, lat]);
          }
        }
        if (!bounds.isEmpty()) {
          const padding = {
            top: (header?.getBoundingClientRect().bottom ?? 0) + 64,
            left: (column?.getBoundingClientRect().right ?? 0) + 64,
            right: 64,
            bottom: 104,
          };
          const camera = instance.cameraForBounds(bounds, {
            padding,
            bearing: 0,
            maxZoom: 17,
          });
          if (camera)
            instance.flyTo({
              ...camera,
              padding,
              retainPadding: false,
              pitch: 0,
              bearing: 0,
              duration:
                useProjectClosing.getState().phase === 'preparing' ||
                window.matchMedia('(prefers-reduced-motion: reduce)').matches
                  ? 0
                  : 900,
              curve: 1,
              easing: (progress) => progress * progress * (3 - 2 * progress),
            });
          return;
        }
      }
      if (!location) {
        const bounds = new mapboxgl.LngLatBounds();
        for (const pin of pins.features) {
          const [lng, lat] = pin.geometry.coordinates;
          if (typeof lng === 'number' && typeof lat === 'number')
            bounds.extend([lng, lat]);
        }
        for (const outline of rawOutlines.features) {
          for (const [lng, lat] of outline.geometry.coordinates.flat(2)) {
            if (typeof lng === 'number' && typeof lat === 'number')
              bounds.extend([lng, lat]);
          }
        }
        if (bounds.isEmpty()) return;
        const padding = {
          top: (header?.getBoundingClientRect().bottom ?? 0) + 64,
          left: (column?.getBoundingClientRect().right ?? 0) + 64,
          right: 64,
          bottom: 104, // 64 px visible margin plus the cropped 40 px.
        };
        const camera = instance.cameraForBounds(bounds, {
          padding,
          bearing: 0,
          maxZoom: 17,
        });
        if (camera) {
          overviewFitZoom.current = camera.zoom;
          const animateReset =
            cameraReset !== appliedCameraReset.current &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          appliedCameraReset.current = cameraReset;
          const target = {
            ...camera,
            zoom: overviewZoom.current ?? camera.zoom,
            padding,
            retainPadding: false,
            pitch: 0,
            bearing: 0,
            duration: animateReset ? 500 : 0,
          };
          if (animateReset)
            instance.flyTo({
              ...target,
              curve: 1,
              easing: (progress) => progress * progress * (3 - 2 * progress),
            });
          else instance.easeTo(target);
        }
        return;
      }
      // A 500 m square around a clicked project, fitted to the free viewport.
      const padding = {
        top: 0,
        right: 0,
        bottom: 0,
        left: Math.max(
          0,
          (column?.getBoundingClientRect().right ?? 0) -
            (container.current?.getBoundingClientRect().left ?? 0),
        ),
      };
      const latDelta = 250 / 111320;
      const lngDelta = latDelta / Math.cos((location.lat * Math.PI) / 180);
      const camera = instance.cameraForBounds(
        [
          [location.lng - lngDelta, location.lat - latDelta],
          [location.lng + lngDelta, location.lat + latDelta],
        ],
        { padding, bearing: 0, maxZoom: 17 },
      );
      if (camera) {
        const reducedMotion =
          useProjectClosing.getState().phase === 'preparing' ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const distance = instance
          .getCenter()
          .distanceTo(new mapboxgl.LngLat(location.lng, location.lat));
        const target = {
          ...camera,
          padding,
          retainPadding: false,
          pitch: 0,
          bearing: 0,
        };
        if (!reducedMotion && distance > 1000) {
          instance.flyTo({ ...target, duration: 1800 });
        } else {
          instance.easeTo({
            ...target,
            duration: reducedMotion ? 0 : 700,
          });
        }
      }
    };
    // Camera fitting uses the map transform and must not wait for tiles or
    // GeoJSON updates, which do not emit the initial load event again.
    move();
    const listColumn = container.current
      ?.closest('[data-project-list-page]')
      ?.querySelector('[data-project-list-column]');
    let observedColumnWidth = listColumn?.clientWidth;
    let observedWidth = container.current?.clientWidth;
    let observedHeight = container.current?.clientHeight;
    const observer = new ResizeObserver(() => {
      const width = container.current?.clientWidth;
      const height = container.current?.clientHeight;
      const columnWidth = listColumn?.clientWidth;
      if (
        width === observedWidth &&
        height === observedHeight &&
        columnWidth === observedColumnWidth
      )
        return;
      observedColumnWidth = columnWidth;
      observedWidth = width;
      observedHeight = height;
      instance.resize();
      move();
    });
    if (container.current) observer.observe(container.current);
    if (listColumn) observer.observe(listColumn);
    return () => {
      observer.disconnect();
    };
  }, [
    location,
    contentReady,
    cameraReset,
    fitProjectId,
    previewRequest,
    pins,
    outlines,
    previewsById,
    rawOutlines,
    config.mapboxAccessToken,
  ]);

  const blurRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const column = container.current
      ?.closest('[data-project-list-page]')
      ?.querySelector('[data-project-list-column]');
    const updateBlur = () => {
      const columns =
        Number(column?.getAttribute('data-project-map-columns')) || 1;
      const width = column?.getBoundingClientRect().width ?? 0;
      const offset = view === 'Map' ? width * (1 - 1 / columns) * 0.75 : 0;
      blurRef.current?.style.setProperty(
        '--project-blur-offset',
        `${offset}px`,
      );
    };
    updateBlur();
    const observer = new ResizeObserver(updateBlur);
    if (column) observer.observe(column);
    return () => observer.disconnect();
  }, [view, contentReady, projects.length]);

  const captionRef = useRef<HTMLSpanElement>(null);
  const captionManuallyDismissed = useRef(false);
  useEffect(() => {
    captionManuallyDismissed.current = false;
  }, [selectedProjectId, previewRequest]);
  const [placeName, setPlaceName] = useState('');
  const opening = useProjectOpening((state) => state.phase !== 'idle');
  const selectedCoordinate = useMemo(() => {
    const preview = selectedProjectId
      ? previewsById.get(selectedProjectId)
      : undefined;
    const bounds = new mapboxgl.LngLatBounds();
    for (const feature of preview?.outlines.features ?? []) {
      for (const point of feature.geometry.coordinates.flat(2)) {
        const [lng, lat] = point;
        if (lng !== undefined && lat !== undefined) bounds.extend([lng, lat]);
      }
    }
    if (!bounds.isEmpty()) {
      const center = bounds.getCenter();
      return { lng: center.lng, lat: center.lat };
    }
    return preview?.location;
  }, [selectedProjectId, previewsById]);
  const captionLng = selectedCoordinate?.lng;
  const captionLat = selectedCoordinate?.lat;
  useEffect(() => {
    setPlaceName('');
    if (
      captionLng === undefined ||
      captionLat === undefined ||
      !config.mapboxAccessToken
    )
      return;
    const key = `${captionLng},${captionLat}`;
    const cached = placeNames.get(key);
    if (cached) {
      setPlaceName(cached);
      return;
    }
    const controller = new AbortController();
    // Product UI is always English — request English place/region labels (see docs/ux/projects-overview.md).
    void fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${key}.json?access_token=${encodeURIComponent(config.mapboxAccessToken)}&types=place&language=en&limit=1`,
      { signal: controller.signal },
    )
      .then(async (response) => {
        if (!response.ok) return;
        const result = locationResponseSchema.safeParse(await response.json());
        if (!result.success || controller.signal.aborted) return;
        const place = result.data.features[0];
        if (!place) return;
        const region = place.context?.find((item) =>
          item.id.startsWith('region.'),
        )?.text;
        const name = region ? `${place.text}, ${region}` : place.text;
        placeNames.set(key, name);
        setPlaceName(name);
      })
      .catch(() => {
        // Missing location metadata must not interrupt map navigation.
      });
    return () => controller.abort();
  }, [captionLng, captionLat, config.mapboxAccessToken]);
  useEffect(() => {
    const instance = map.current;
    if (!instance || captionLng === undefined || captionLat === undefined)
      return;
    const position = () => {
      const caption = captionRef.current;
      if (!caption) return;
      const point = instance.project([captionLng, captionLat]);
      const height = viewport.current?.clientHeight ?? window.innerHeight;
      caption.style.left = `${point.x}px`;
      const captionInset =
        (viewport.current?.getBoundingClientRect().top ?? 0) +
        16 +
        caption.getBoundingClientRect().height / 2;
      caption.style.top = `calc(var(--organization-agent-dock-top, ${height - 48 + captionInset}px) - ${captionInset}px)`;
      caption.style.visibility =
        !captionManuallyDismissed.current &&
        point.x >= 0 &&
        point.x <= instance.getContainer().clientWidth &&
        point.y >= 0 &&
        point.y < height
          ? 'visible'
          : 'hidden';
    };
    const onMoveStart = (event: { originalEvent?: unknown }) => {
      // User navigation leaves the selected project's framed view. A new
      // programmatic framing (including reselecting it) restores the caption.
      captionManuallyDismissed.current = Boolean(event.originalEvent);
      position();
    };
    position();
    instance.on('movestart', onMoveStart);
    instance.on('move', position);
    instance.on('resize', position);
    return () => {
      instance.off('movestart', onMoveStart);
      instance.off('move', position);
      instance.off('resize', position);
    };
  }, [
    captionLng,
    captionLat,
    config.mapboxAccessToken,
    placeName,
    selectedProjectId,
    previewRequest,
  ]);

  const blurMask =
    'linear-gradient(to right, black var(--project-blur-offset, 0px), rgba(0,0,0,0.75) calc(var(--project-blur-offset, 0px) + 12.5%), rgba(0,0,0,0.5) calc(var(--project-blur-offset, 0px) + 25%), rgba(0,0,0,0.25) calc(var(--project-blur-offset, 0px) + 37.5%), transparent calc(var(--project-blur-offset, 0px) + 50%))';

  return (
    <ProjectMapContext.Provider value={preview}>
      <SelectedProjectMapContext.Provider value={selectedProjectId}>
        <Box
          ref={viewport}
          sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden' }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: '-40px',
              opacity: opening || view !== 'List' ? 1 : 0.3,
              transition: 'opacity 150ms ease',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
            }}
          >
            <Box
              ref={container}
              data-project-list-map=""
              sx={{ width: '100%', height: '100%' }}
            />
          </Box>
          <Typography
            ref={captionRef}
            component="span"
            variant="h5"
            data-project-map-place=""
            sx={{
              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              fontWeight: 400,
              color: 'rgba(0,0,0,0.45)',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              opacity: opening ? 0 : view === 'List' ? 0.3 : 1,
              transition: 'opacity 150ms ease',
              zIndex: 1,
            }}
          >
            {placeName}
          </Typography>
          <Box
            ref={blurRef}
            data-project-list-blur=""
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              opacity: opening ? 0 : 1,
              transition: 'opacity 150ms ease',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
              backdropFilter: 'blur(30px)',
              WebkitBackdropFilter: 'blur(30px)',
              maskImage: blurMask,
              WebkitMaskImage: blurMask,
            }}
          />
          <Tooltip
            title={
              openTooltipPoint?.projectNames ? (
                <Box>
                  {openTooltipPoint.projectNames.map((name, index) => (
                    <Box key={index} sx={{ whiteSpace: 'nowrap' }}>
                      {name}
                    </Box>
                  ))}
                </Box>
              ) : (
                'Open project'
              )
            }
            open={!!openTooltipPoint}
            placement="top"
            disableInteractive
            slotProps={{
              tooltip: {
                sx: openTooltipPoint?.projectNames
                  ? { maxWidth: 'none' }
                  : undefined,
              },
              popper: {
                anchorEl: openTooltipPoint
                  ? {
                      getBoundingClientRect: () =>
                        new DOMRect(
                          openTooltipPoint.x,
                          openTooltipPoint.y,
                          0,
                          0,
                        ),
                    }
                  : undefined,
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: 1,
                height: 1,
                pointerEvents: 'none',
              }}
            />
          </Tooltip>
        </Box>
        {children}
      </SelectedProjectMapContext.Provider>
    </ProjectMapContext.Provider>
  );
};
